/**
 * SSE Client — 前端 Server-Sent Events 客户端
 *
 * 使用 fetch + ReadableStream 读取 SSE 事件流。
 * Auth token 从 wortou/browser 的 localStorage 获取。
 */
import { getUserToken } from 'wortou/browser';
import type { SenderRole } from '@shared/types/gameState';
import type { RoundSummary } from '@shared/types/agent';

export interface SSECallbacks {
  onSpeechStart?: (agentId: string, agentName: string, agentRole: SenderRole) => void;
  onSpeechChunk?: (agentId: string, text: string) => void;
  onSpeechEnd?: (agentId: string, fullText: string) => void;
  onRoundComplete?: (summary: RoundSummary) => void;
  onSummaryUpdate?: (summary: string) => void;
  onError?: (error: Error) => void;

  // Card narrative events
  onCardNarrativeChunk?: (text: string) => void;
  onCardNarrativeEnd?: (cardName: string, cardType: string, fullText: string) => void;

  // Act3-specific events
  onWeekNarrative?: (data: any) => void;
  onMonologueStart?: (side: 'A' | 'B', name: string) => void;
  onMonologueChunk?: (side: 'A' | 'B', text: string) => void;
  onMonologueEnd?: (side: 'A' | 'B', fullText: string) => void;
  onWeekComplete?: (data: any) => void;
}

/**
 * 连接 SSE 端点，解析事件流并触发回调
 *
 * @returns AbortController 用于中断连接
 */
export function connectSSE(
  url: string,
  body: unknown,
  callbacks: SSECallbacks,
): AbortController {
  const abortController = new AbortController();

  (async () => {
    try {
      const token = getUserToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Wortou-User-Token'] = token;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      if (!response.ok) {
        // 401/402 需要特殊处理（登录/付费）
        if (response.status === 401 || response.status === 402) {
          const errBody = await response.json().catch(() => ({}));
          callbacks.onError?.(Object.assign(new Error(`HTTP ${response.status}`), { status: response.status, body: errBody }));
          return;
        }
        throw new Error(`SSE request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 按双换行分割 SSE 事件
        const events = buffer.split('\n\n');
        // 最后一个可能不完整，保留在 buffer 中
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;
          parseSSEEvent(eventBlock, callbacks);
        }
      }

      // 处理 buffer 中剩余的数据
      if (buffer.trim()) {
        parseSSEEvent(buffer, callbacks);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      callbacks.onError?.(err as Error);
    }
  })();

  return abortController;
}

/**
 * 解析单个 SSE 事件块
 */
function parseSSEEvent(block: string, callbacks: SSECallbacks): void {
  let eventType = '';
  let dataStr = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      dataStr += line.slice(6);
    } else if (line.startsWith('data:')) {
      dataStr += line.slice(5);
    }
  }

  if (!eventType || !dataStr) return;

  let data: any;
  try {
    data = JSON.parse(dataStr);
  } catch {
    console.warn('[SSE] Failed to parse event data:', dataStr);
    return;
  }

  switch (eventType) {
    case 'speech_start':
      callbacks.onSpeechStart?.(data.agentId, data.agentName, data.agentRole);
      break;
    case 'speech_chunk':
      callbacks.onSpeechChunk?.(data.agentId, data.text);
      break;
    case 'speech_end':
      callbacks.onSpeechEnd?.(data.agentId, data.fullText);
      break;
    case 'round_complete':
      callbacks.onRoundComplete?.(data);
      break;
    case 'summary_update':
      callbacks.onSummaryUpdate?.(data.summary);
      break;
    case 'error':
      callbacks.onError?.(new Error(data.message || 'SSE stream error'));
      break;
    case 'done':
      // 流结束
      break;

    // Card narrative events
    case 'card_narrative_chunk':
      callbacks.onCardNarrativeChunk?.(data.text);
      break;
    case 'card_narrative_end':
      callbacks.onCardNarrativeEnd?.(data.cardName, data.cardType, data.fullText);
      break;

    // Act3-specific events
    case 'week_narrative':
      callbacks.onWeekNarrative?.(data);
      break;
    case 'monologue_start':
      callbacks.onMonologueStart?.(data.side, data.name);
      break;
    case 'monologue_chunk':
      callbacks.onMonologueChunk?.(data.side, data.text);
      break;
    case 'monologue_end':
      callbacks.onMonologueEnd?.(data.side, data.fullText);
      break;
    case 'week_complete':
      callbacks.onWeekComplete?.(data);
      break;

    default:
      console.warn('[SSE] Unknown event type:', eventType);
  }
}
